// -----------------------------------------------------------------------
// <copyright company="Microsoft Corporation">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------
import type {
  IDeferredPlaybackSession,
  IEmbedLoader,
  IEmbedLoaderOptions,
  IEmbedPlaybackSessionOptions,
  IPlaybackApi,
  IPlaybackSession,
  IThumbnailHyperlinkOnlyPlaybackSession,
  ITokenRetriever,
  LoadMode,
  RelativeTimestamp
} from '../types/external';
import type { IIframeApi, OnPreSessionErrorCallback } from '../types/internal';
import { ObservableProperty } from './observable';
import { OnePlayerError } from './onePlayerError';
import { OnePlayerIframe } from './onePlayerIframe';

export abstract class OnePlayerLoaderBase implements IEmbedLoader {
  private readonly _loaderOptionsBase: IEmbedLoaderOptions;

  public constructor(loaderOptions: IEmbedLoaderOptions) {
    this._loaderOptionsBase = loaderOptions;
  }

  public async createPlaybackSession(
    isAutoplayOn: boolean,
    playbackSessionOptions: IEmbedPlaybackSessionOptions
  ): Promise<IPlaybackSession> {
    try {
      const [iframeApi, dispose] = await this.createPlaybackSessionInternal(
        isAutoplayOn ? 'autoplayOn' : 'autoplayOff',
        playbackSessionOptions
      );

      return {
        playbackApi: iframeApi.externalPlayerApi,
        dispose,
        playerStats: iframeApi.playerStats
      };
    } catch (error: unknown) {
      if (error instanceof OnePlayerError) {
        throw error;
      } else {
        throw OnePlayerError.createGenericError(
          'fatal',
          'loadIframe',
          'iframeUnknown',
          error,
          undefined,
          playbackSessionOptions.hostSessionId
        );
      }
    }
  }

  public async createThumbnailHyperlinkOnlySession(
    playbackSessionOptions: IEmbedPlaybackSessionOptions
  ): Promise<IThumbnailHyperlinkOnlyPlaybackSession> {
    try {
      const [iframeApi, dispose] = await this.createPlaybackSessionInternal(
        'thumbnailHyperlinkOnly',
        playbackSessionOptions
      );

      return {
        playbackApi: iframeApi.externalPlayerApi,
        playerStats: iframeApi.playerStats,
        dispose,
        simulateClick: () => iframeApi.loadPlayer(true)
      };
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async createDeferredPlaybackSession(
    playbackSessionOptions: IEmbedPlaybackSessionOptions
  ): Promise<IDeferredPlaybackSession> {
    try {
      const [iframeApi, dispose] = await this.createPlaybackSessionInternal(
        'deferredLoad',
        playbackSessionOptions
      );

      const playbackApiObservable = new ObservableProperty<IPlaybackApi | undefined>(undefined);

      // When the player is ready, share the playbackApi with the host
      iframeApi.externalPlayerApi.isPlayerReady.subscribe((isReady: boolean) => {
        if (isReady) {
          playbackApiObservable.value = iframeApi.externalPlayerApi;
        }
      });

      return {
        playbackApi: playbackApiObservable,
        loadPlayer: (triggerPlayClick?: boolean | undefined) => iframeApi.loadPlayer(triggerPlayClick),
        warmData: () => iframeApi.warmData(),
        dispose,
        playerStats: iframeApi.playerStats
      };
    } catch (error: unknown) {
      if (error instanceof OnePlayerError) {
        error.hostSessionId = playbackSessionOptions.hostSessionId;
        throw error;
      } else {
        throw OnePlayerError.createGenericError(
          'fatal',
          'loadIframe',
          'iframeUnknown',
          error,
          undefined,
          playbackSessionOptions.hostSessionId
        );
      }
    }
  }

  protected abstract adjustEmbedUrlQueryString(
    loadMode: LoadMode,
    embedUrl: string,
    playbackSessionOptions: IEmbedPlaybackSessionOptions,
    createSessionTimestamp: RelativeTimestamp
  ): string;

  protected abstract getEmbedUrlAsync(videoUrl: string): Promise<string>;

  protected createOnePlayerIframe(
    embedUrl: string,
    containerDiv: HTMLDivElement,
    tokenRetriever: ITokenRetriever,
    onError: OnPreSessionErrorCallback,
    allowFullScreen?: boolean
  ): OnePlayerIframe {
    return new OnePlayerIframe(embedUrl, containerDiv, tokenRetriever, onError, allowFullScreen);
  }

  private async createPlaybackSessionInternal(
    loadMode: LoadMode,
    playbackSessionOptions: IEmbedPlaybackSessionOptions
  ): Promise<[IIframeApi, () => void]> {
    const msSinceStartOfCurrentDoc = performance.now();
    const createSessionTimestamp: RelativeTimestamp = {
      // For host pages that have been open for a long time, it is most accurate to re-calculate the 'time origin' based on
      //  current notion of 'milliseconds' before using it to compare against other time origins.
      // timeOrigin = msSinceEpochAtStartOfCurrentDoc = msSinceEpoch - msSinceStartOfCurrentDoc
      // See stream/components/WebVideo/src/V2/OnePlayer/types/hostTimestamps.types.ts for more details
      timeOrigin: Date.now() - msSinceStartOfCurrentDoc,
      timestamp: msSinceStartOfCurrentDoc
    };
    let embedUrl = await this.getEmbedUrlAsync(playbackSessionOptions.videoId.videoUrl);
    embedUrl = this.adjustEmbedUrlQueryString(
      loadMode,
      embedUrl,
      playbackSessionOptions,
      createSessionTimestamp
    );

    let iFrame: OnePlayerIframe | undefined = undefined;

    // Create a dispose function that centralizes all the work to dispose a session
    const dispose = () => {
      try {
        iFrame?.dispose();
      } catch (error: unknown) {
        if (error instanceof OnePlayerError) {
          throw error;
        } else {
          throw OnePlayerError.createGenericError(
            'fatal',
            'dispose',
            'disposeUnknown',
            error,
            undefined,
            playbackSessionOptions.hostSessionId
          );
        }
      }
    };

    // Since OnErrorCallback requires a dispose function and it has a dependency on the OnePlayerIframe
    // having been created, we pass a OnPreSessionErrorCallback into OnePlayerIframe instead for it to call
    // if an error occurs
    const onError: OnPreSessionErrorCallback = (error: OnePlayerError) => {
      playbackSessionOptions.onError(dispose, error);
    };

    // Create the OnePlayerIframe
    iFrame = this.createOnePlayerIframe(
      embedUrl,
      playbackSessionOptions.containerDiv,
      this._loaderOptionsBase.tokenRetriever,
      onError,
      playbackSessionOptions.allowFullscreen
    );

    // Navigate the iframe to OnePlayer and connect to it
    const iFrameApi = await iFrame.loadAndConnectToOnePlayer();
    iFrameApi.loadAndConnectToOnePlayerCallback = iFrame.loadAndConnectToOnePlayer.bind(iFrame);

    return [iFrameApi, dispose];
  }
}
